import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { TEAM_AVATARS } from './teamAvatars';

/* Every AI teammate recommends one thing — colored, tappable card per agent.
   Order: Eva → Rachel → Penny → Sonny → Stan (Linda stays in Legal).
   Cards navigate to the agent's area (backend `to`) or fire onCardClick. */

const API = process.env.REACT_APP_BACKEND_URL;

export default function AgentAdviceRail({ audience = 'demo', isEs, className = '', onCardClick }) {
  const [cards, setCards] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    fetch(`${API}/api/agents/advice?audience=${audience}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => setCards(d.cards || []))
      .catch(() => setCards([]));
  }, [audience]);

  if (cards && cards.length === 0) return null;
  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3 ${className}`} data-testid={`agent-advice-rail-${audience}`}>
      {!cards
        ? [0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="h-28 rounded-2xl animate-pulse" style={{ background: 'rgba(148,163,184,0.12)' }} />
        ))
        : cards.map((c, i) => {
          const av = TEAM_AVATARS.find((a) => a.key === c.agent);
          const clickable = Boolean(c.to || onCardClick);
          const go = () => {
            if (onCardClick) onCardClick(c);
            else if (c.to) navigate(c.to);
          };
          return (
            <button key={c.agent} type="button" data-testid={`agent-advice-${c.agent}`}
              onClick={clickable ? go : undefined} disabled={!clickable}
              className={`group text-left rounded-2xl p-3.5 transition-all duration-200 ${clickable ? 'cursor-pointer hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus:ring-2' : 'cursor-default'}`}
              style={{ background: `${c.color}12`, border: `1px solid ${c.color}45`, animation: 'kx-advice-in 0.5s ease both', animationDelay: `${i * 70}ms` }}>
              <div className="flex items-center gap-2">
                {av?.img && <img src={av.img} alt={av.name} className="w-8 h-8 rounded-full object-cover shrink-0"
                  style={{ border: `2px solid ${c.color}` }} />}
                <div className="min-w-0 flex-1">
                  <p className="text-[12px] font-bold leading-tight capitalize" style={{ color: c.color }}>{c.agent}</p>
                  <p className="text-[9.5px] uppercase tracking-wide font-bold opacity-60" style={{ color: c.color }}>
                    {isEs ? c.role_es : c.role_en}
                  </p>
                </div>
                {clickable && (
                  <ArrowRight className="w-3.5 h-3.5 shrink-0 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200"
                    style={{ color: c.color }} data-testid={`agent-advice-${c.agent}-arrow`} />
                )}
              </div>
              <p className="mt-2 text-[11.5px] leading-relaxed" style={{ color: 'var(--os-ink, #374151)' }}>
                {isEs ? c.es : c.en}
              </p>
              {clickable && (
                <p className="mt-1.5 text-[9.5px] font-bold uppercase tracking-wide opacity-0 group-hover:opacity-70 transition-opacity" style={{ color: c.color }}>
                  {isEs ? 'Abrir área' : 'Open area'} →
                </p>
              )}
            </button>
          );
        })}
      <style>{'@keyframes kx-advice-in { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }'}</style>
    </div>
  );
}
